import React from 'react';
import { Form } from 'antd';
import { MinusCircleOutlined } from '@ant-design/icons';
import { FormItem } from '@xbcb/form-item-components';
import { NamePath } from '@xbcb/ui-types';
import { RecordType } from '@xbcb/shared-types';
import { useTheme } from 'styled-components';
import { EntityRoleCode } from '__generated__/graphql';
import RecordSelect from 'components/RecordSelect';
import LocationSummary from 'components/LocationSummary';
import {
  StyledSection,
  StyledSectionHeader,
  StyledNamesRow,
  StyledRemoveButton,
  StyledSelect,
  StyledFacilityColumn,
  StyledAddressSummary,
} from './styles';

const ENTITY_ROLE_CODE_OPTIONS = [
  { value: EntityRoleCode.MF, label: 'MF - Manufacturer of good' },
  {
    value: EntityRoleCode.ITL,
    label: 'ITL - Independent Third Party Laboratory',
  },
  { value: EntityRoleCode.LAB, label: 'LAB - Laboratory' },
  { value: EntityRoleCode.NOL, label: 'NOL - No lab testing required' },
  { value: EntityRoleCode.CE, label: 'CE - Certifying Entity' },
  { value: EntityRoleCode.PK, label: 'PK - Point of Contact' },
];

export interface CpscEntityDetailsProps {
  /**
   * Relative path within Form.List (e.g., [fieldName]).
   * Used for FormItem name binding within the list's scope.
   */
  localNamePath: NamePath;
  /**
   * Absolute path from form root (e.g., ['cpsc', 'items', 0, 'entities', 0]).
   * Required for RecordSelect and Form.useWatch which need full path context.
   */
  fullNamePath: NamePath;
  /** Zero-based entity index; UI displays as 1-based ("Facility Details - 1") */
  index: number;
  /** When true, renders the Remove button. Typically false for index=0 (first entity) */
  showRemove?: boolean;
  /** Callback to remove this entity from the Form.List array */
  onRemove?: () => void;
  /** Disables editing and hides Remove button; fields remain visible but non-interactive */
  readOnly?: boolean;
  /** Grays out and disables all inputs; different from readOnly which hides controls */
  disabled?: boolean;
}

/**
 * CpscEntityDetails - Generic entity component for CPSC eFiling
 *
 * Renders:
 * - Section header with "Entity Details - N" title
 * - Entity Role Code dropdown
 * - Entity Name (Facility picker using RecordSelect)
 * - Address display (when facility is selected)
 * - Remove button (when showRemove is true)
 *
 * Designed to be used with FormListSection for add/remove functionality.
 * @return {JSX.Element} The entity details form section
 */
const CpscEntityDetails: React.FC<CpscEntityDetailsProps> = ({
  localNamePath,
  fullNamePath,
  index,
  showRemove = false,
  onRemove,
  readOnly = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const form = Form.useFormInstance();

  // Watch facility field to get selected facility ID for address display
  const facilityPath = [...(fullNamePath as (string | number)[]), 'facility'];
  const facility = Form.useWatch(facilityPath, form);
  const facilityId = facility?.id;

  return (
    <StyledSection>
      {/* Section Header */}
      <StyledSectionHeader>
        <span>Facility Details - {index + 1}</span>
        {showRemove && !readOnly && (
          <StyledRemoveButton
            type="text"
            danger
            icon={<MinusCircleOutlined />}
            onClick={onRemove}
            disabled={disabled}
          >
            Remove
          </StyledRemoveButton>
        )}
      </StyledSectionHeader>

      {/* Entity Role Code Dropdown */}
      <StyledNamesRow>
        <FormItem
          name={[...(localNamePath as (string | number)[]), 'entityRoleCode']}
          label="Facility Role Code"
          $itemSize={theme.size.short_medium}
          $readOnly={readOnly}
        >
          <StyledSelect
            options={ENTITY_ROLE_CODE_OPTIONS}
            disabled={disabled || readOnly}
            placeholder="Select role code"
          />
        </FormItem>
      </StyledNamesRow>

      {/* Entity Name (Facility Picker using RecordSelect) */}
      <StyledNamesRow>
        <StyledFacilityColumn>
          <Form.Item noStyle>
            <RecordSelect
              recordType={RecordType.FACILITY}
              $itemSize={theme.size.short_medium}
              form={form}
              localNamePath={[
                ...(localNamePath as (string | number)[]),
                'facility',
              ]}
              fullNamePath={[
                ...(fullNamePath as (string | number)[]),
                'facility',
              ]}
              disabled={disabled}
              label="Facility Name"
              readOnly={readOnly}
              required={true}
              create={true}
              placeHolder="Select Facility"
            />
          </Form.Item>
          {/* Address Display (when facility is selected) */}
          {facilityId && (
            <StyledAddressSummary>
              <LocationSummary recordId={facilityId} />
            </StyledAddressSummary>
          )}
        </StyledFacilityColumn>
      </StyledNamesRow>
    </StyledSection>
  );
};

export default CpscEntityDetails;
